import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import {
    Box,
    Button,
    Container,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid,
    IconButton,
    Paper,
    Stack,
    Typography,
    Collapse,
    Chip,
    useTheme,
    useMediaQuery
} from '@mui/material';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import CloseIcon from '@mui/icons-material/Close';
import CheckIcon from '@mui/icons-material/Check';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import DriveFileRenameOutlineIcon from '@mui/icons-material/DriveFileRenameOutline';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import axios from 'axios';

import 'Styles/App.scss';
import UserContextP from 'Interfaces/Context';
import Loader from 'Components/Loader';
import DialogConfirmation from 'Components/DialogConfirmation';
import { CssTextField } from 'Components/CssTextField';
import Header from 'Components/DetailDocument/Header';
import TableProducts from 'Components/DetailDocument/TableProducts';
import InformationDocument from 'Components/DetailDocument/InformationDocument';
import { useNavigate } from 'react-router-dom';
import { resolveCityFromOrder } from './LocationHelper';

const DetailDocument: React.FC = () => {
    const context = useContext(UserContextP);
    const navigate = useNavigate();

    const [orderLocal, setOrderLocal] = useState<any>(null);
    const [costumer, setCostumer] = useState<any>(null);
    const [clientAddress, setClientAddress] = useState<any>(null);
    const [text, setText] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(false);
    const [openResultDialog, setOpenResultDialog] = useState(false);
    const [resultMessage, setResultMessage] = useState<{ message: string; type: 'success' | 'error' }>({ message: '', type: 'success' });
    const [confirmAction, setConfirmAction] = useState<null | 'approve' | 'reject'>(null);
    const [openConfirm, setOpenConfirm] = useState(false);

    const order = useMemo(() => orderLocal ?? context?.dataCopy?.order ?? null, [orderLocal, context?.dataCopy?.order]);
    const badgeColor = useMemo(() => (order?.type === 'PurchaseOrder' ? '#79AC78' : '#173456'), [order?.type]);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const [showSummaryMobile, setShowSummaryMobile] = useState(true);

    // Keep the default: visible on desktop, collapsed on mobile
    useEffect(() => {
        setShowSummaryMobile(!isMobile);
    }, [isMobile]);

    useEffect(() => {
        document.title = 'Actualizar estado';
        setOrderLocal(context?.dataCopy?.order ?? null);
        if (!context?.dataCopy?.order) {
            navigate('/update/order/document', { replace: true });
            return;
        }

        const controller = new AbortController();
        fetchCostumer(controller.signal).catch(() => { });
        return () => controller.abort();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [context?.dataCopy?.order]);

    const fetchCostumer = useCallback(
        async (signal?: AbortSignal) => {
            try {
                const token = context?.authenticate?.token;
                const cardCode = order?.cardCode ?? context?.dataCopy?.order?.cardCode;
                if (!token || !cardCode) return;

                const [clientRes, addressRes] = await Promise.allSettled([
                    axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/client`, {
                        params: { cardCode },
                        headers: { Authorization: `Bearer ${token}` },
                        signal
                    }),
                    axios.get(`${process.env.REACT_APP_URL_ORDER}/api/v1/client/adress`, {
                        params: { clientId: cardCode },
                        headers: { Authorization: `Bearer ${token}` },
                        signal
                    })
                ]);

                if (clientRes.status === 'fulfilled') {
                    setCostumer(clientRes.value.data?.data?.[0] ?? null);
                }

                if (addressRes.status === 'fulfilled') {
                    const addresses: any[] = addressRes.value.data?.data || [];
                    const orderTargetCode = typeof order?.payToCode === 'string'
                        ? order.payToCode
                        : (order?.payToCode?.location || order?.address || '');

                    const match = addresses.find(
                        (a: any) => a.address && orderTargetCode && String(a.address).trim().toLowerCase() === String(orderTargetCode).trim().toLowerCase()
                    ) || addresses[0] || null;

                    setClientAddress(match);
                }
            } catch (err) {
                console.debug('fetchCostumer error', err);
                setCostumer(null);
                setClientAddress(null);
            }
        },
        [context?.authenticate?.token, order?.cardCode, order?.payToCode, order?.address, context?.dataCopy?.order]
    );

    const resolvedCity = useMemo(
        () => resolveCityFromOrder(order, clientAddress, costumer),
        [order, clientAddress, costumer]
    );

    const submitStatus = useCallback(
        async (approve: boolean) => {
            setLoading(true);
            try {
                const token = context?.authenticate?.token;
                if (!token) throw new Error('No autorizado');
                const payload = { state: approve, id: context?.dataCopy?.order?.$key, notes: text };
                const res = await axios.put(`${process.env.REACT_APP_URL_ORDER}/api/v1/orders/update/status`, payload, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setResultMessage({ message: res.data?.message ?? (approve ? 'Orden aprobada' : 'Orden rechazada'), type: 'success' });
            } catch (err: any) {
                console.debug('submitStatus error', err);
                const serverData = err?.response?.data;
                const errorMsg =
                    (typeof serverData?.message === 'string' && serverData.message.trim() !== '' ? serverData.message : null) ||
                    (Array.isArray(serverData?.errorMessage) && serverData.errorMessage.length > 0
                        ? serverData.errorMessage.filter(Boolean).join('. ')
                        : null);

                const msg =
                    errorMsg ||
                    (err?.response?.status === 422 || err?.response?.status === 500
                        ? 'No se pudo completar la sincronización con SAP en este momento. Por favor verifique y reintente nuevamente en unos minutos.'
                        : 'Error al procesar la orden. Contacte soporte.');

                const isAlready = typeof msg === 'string' && msg.includes('Orden ya autorizada');
                setResultMessage({ message: msg, type: isAlready ? 'success' : 'error' });
            } finally {
                setLoading(false);
                setOpenResultDialog(true);
                setOpenConfirm(false);
                setConfirmAction(null);
            }
        },
        [context?.authenticate?.token, context?.dataCopy?.order, text]
    );

    return (
        <>
            <Loader openLoader={loading} inPage={false} redirect={true} />
            <DialogConfirmation
                open={openResultDialog}
                text={resultMessage.message}
                quantity={-1}
                type={resultMessage.type}
                onClose={() => setOpenResultDialog(false)}
            />

            <Container maxWidth="xl" sx={{ py: { xs: 1.5, md: 3 }, px: { xs: 1, md: 3 }, minHeight: '100vh' }}>
                <Paper
                    elevation={0}
                    sx={{
                        display: 'flex',
                        flexDirection: { xs: 'column', md: 'row' },
                        gap: 2.5,
                        p: { xs: 2, md: 3 },
                        borderRadius: 3,
                        border: '1px solid #e2e8f0',
                        boxShadow: '0 4px 20px rgba(17, 42, 86, 0.05)',
                        bgcolor: '#ffffff'
                    }}
                >
                    <Box sx={{ flex: 1, overflow: 'auto', pr: { xs: 0, md: 1 } }}>
                        {/* Top Header with Back Button, Order Number, Date and Status */}
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <IconButton
                                    onClick={() => navigate('/update/order/document')}
                                    aria-label="Volver a la lista de órdenes"
                                    sx={{
                                        bgcolor: '#f1f5f9',
                                        color: '#112a56',
                                        width: 42,
                                        height: 42,
                                        mr: 1.5,
                                        borderRadius: '50px',
                                        transition: 'all 0.2s ease',
                                        '&:hover': {
                                            bgcolor: '#e2e8f0',
                                            transform: 'translateX(-2px)'
                                        }
                                    }}
                                >
                                    <ArrowBackIosNewIcon sx={{ fontSize: 18, ml: '1px' }} />
                                </IconButton>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 800, color: '#112a56', letterSpacing: -0.5 }}>
                                        Orden #{order?.numberOrder ?? 0}
                                    </Typography>
                                    <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                                        {order?.docDate ? new Date(order.docDate).toLocaleString() : ''}
                                    </Typography>
                                </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: { xs: 'flex-start', md: 'flex-end' }, gap: 0.5 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                    {/* Insignia: Con descuento */}
                                    {(Number(order?.discount ?? 0) > 0 || Number(order?.descuento ?? 0) > 0) && (
                                        <Chip
                                            icon={<LocalOfferIcon sx={{ fontSize: '14px !important', color: '#ffffff !important' }} />}
                                            label="Con descuento"
                                            size="small"
                                            sx={{
                                                bgcolor: '#207435',
                                                color: '#ffffff',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                height: 28,
                                                borderRadius: '50px',
                                                px: 0.5,
                                                boxShadow: '0 1px 4px rgba(32, 116, 53, 0.25)'
                                            }}
                                        />
                                    )}

                                    {/* Insignia: Tipo de documento (Crédito Fiscal / Factura) */}
                                    <Chip
                                        label={
                                            order?.typeDocument === 'CRF' || order?.facSerie === 'CCF'
                                                ? 'Crédito Fiscal'
                                                : order?.typeDocument === 'COF' || order?.facSerie === 'FAC'
                                                ? 'Factura'
                                                : (order?.typeDocument || 'Crédito Fiscal')
                                        }
                                        size="small"
                                        sx={{
                                            bgcolor: '#ffffff',
                                            border: '1.5px solid #dbeafe',
                                            color: '#1e293b',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            height: 28,
                                            borderRadius: '50px',
                                            px: 0.5
                                        }}
                                    />

                                    {/* Insignia: Condición de pago (ej. CREDITO 30 DÍAS) */}
                                    {order?.typePayment && (
                                        <Chip
                                            label={String(order.typePayment).toUpperCase()}
                                            size="small"
                                            sx={{
                                                bgcolor: '#f0fdf4',
                                                border: '1.5px solid #86efac',
                                                color: '#15803d',
                                                fontWeight: 700,
                                                fontSize: '12px',
                                                height: 28,
                                                borderRadius: '50px',
                                                px: 0.5
                                            }}
                                        />
                                    )}

                                    {/* Insignia de Estado */}
                                    {order?.canceled ? (
                                        <Typography
                                            sx={{
                                                background: '#fef2f2',
                                                color: '#dc2626',
                                                border: '1px solid #fecaca',
                                                px: 1.8,
                                                py: 0.5,
                                                borderRadius: '50px',
                                                fontWeight: 700,
                                                fontSize: '12px'
                                            }}
                                        >
                                            Cancelada
                                        </Typography>
                                    ) : order?.saveOrderSap ? (
                                        <Typography
                                            sx={{
                                                background: '#f0fdf4',
                                                color: '#15803d',
                                                border: '1px solid #bbf7d0',
                                                px: 1.8,
                                                py: 0.5,
                                                borderRadius: '50px',
                                                fontWeight: 700,
                                                fontSize: '12px'
                                            }}
                                        >
                                            Procesada en SAP
                                        </Typography>
                                    ) : (
                                        <Typography
                                            sx={{
                                                background: '#fffbeb',
                                                color: '#b45309',
                                                border: '1px solid #fde68a',
                                                px: 1.8,
                                                py: 0.5,
                                                borderRadius: '50px',
                                                fontWeight: 700,
                                                fontSize: '12px'
                                            }}
                                        >
                                            Pendiente de revisión
                                        </Typography>
                                    )}

                                    {/* Tipo de Orden (SalesOrder / PurchaseOrder) */}
                                    <Typography
                                        sx={{
                                            background: badgeColor,
                                            color: '#fff',
                                            px: 2,
                                            py: 0.6,
                                            borderRadius: '50px',
                                            fontWeight: 700,
                                            fontSize: '12px',
                                            letterSpacing: 0.5,
                                            boxShadow: '0 2px 8px rgba(17, 42, 86, 0.15)'
                                        }}
                                    >
                                        {order?.type || 'SalesOrder'}
                                    </Typography>

                                    {isMobile && (
                                        <Button
                                            size="small"
                                            variant="outlined"
                                            startIcon={showSummaryMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                            onClick={() => setShowSummaryMobile(s => !s)}
                                            aria-label={showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                                            sx={{ textTransform: 'none', borderRadius: '50px', borderColor: '#cbd5e1', color: '#475569' }}
                                        >
                                            Resumen
                                        </Button>
                                    )}
                                </Box>

                                {/* Código de ubicación / Sucursal y Ciudad alineado a la derecha */}
                                {(order?.payToCode || order?.address || resolvedCity) && (
                                    <Typography
                                        sx={{
                                            color: '#8c959f',
                                            fontSize: '15px',
                                            fontWeight: 500,
                                            textAlign: 'right',
                                            letterSpacing: 0.5,
                                            pr: 0.5
                                        }}
                                    >
                                        {typeof order?.payToCode === 'string'
                                            ? order.payToCode
                                            : (order?.payToCode?.location || order?.address || '')}
                                        {resolvedCity && (
                                            <Typography component="span" sx={{ color: '#047857', fontWeight: 700, ml: 1 }}>
                                                • {resolvedCity}
                                            </Typography>
                                        )}
                                    </Typography>
                                )}
                            </Box>
                        </Box>

                        {/* Customer and financial info cards */}
                        <Grid container spacing={1.5}>
                            <Header order={order} costumer={costumer} clientAddress={clientAddress} color={badgeColor} />
                        </Grid>

                        {/* Table of products */}
                        <Box sx={{ mt: 2.5 }}>
                            <Grid container>
                                <TableProducts order={order} color={badgeColor} />
                            </Grid>
                        </Box>

                        {/* Order Comments */}
                        <Box sx={{ mt: 2.5 }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2.5,
                                    borderRadius: 2.5,
                                    border: '1px solid #e2e8f0',
                                    bgcolor: '#f8fafc'
                                }}
                            >
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                    <ChatBubbleOutlineIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                    <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                        Comentarios
                                    </Typography>
                                </Stack>
                                <Typography
                                    variant="body1"
                                    sx={{
                                        color: order?.comments && String(order.comments).trim() !== '' ? '#1e293b' : '#94a3b8',
                                        fontStyle: order?.comments && String(order.comments).trim() !== '' ? 'normal' : 'italic',
                                        whiteSpace: 'pre-wrap',
                                        lineHeight: 1.6
                                    }}
                                >
                                    {order?.comments && String(order.comments).trim() !== '' ? order.comments : 'Sin comentarios'}
                                </Typography>
                            </Paper>
                        </Box>

                        {/* Notes for Seller */}
                        <Box sx={{ mt: 2.5 }}>
                            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                                <DriveFileRenameOutlineIcon sx={{ fontSize: 20, color: '#64748b' }} />
                                <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                                    Notas para el vendedor
                                </Typography>
                            </Stack>
                            <CssTextField
                                placeholder="Escribe alguna nota u observación relevante..."
                                rows={4}
                                multiline
                                fullWidth
                                value={text}
                                onChange={(e: any) => setText(e.target.value)}
                            />
                        </Box>
                    </Box>

                    {/* Mobile: show a toggle to reveal/hide the summary */}
                    <Box sx={{ display: { xs: 'flex', md: 'none' }, justifyContent: 'center' }}>
                        <Button
                            size="small"
                            variant="outlined"
                            endIcon={showSummaryMobile ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                            onClick={() => setShowSummaryMobile(s => !s)}
                            aria-label={showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                            sx={{ textTransform: 'none', borderRadius: '50px', borderColor: '#cbd5e1', color: '#475569' }}
                        >
                            {showSummaryMobile ? 'Ocultar resumen' : 'Mostrar resumen'}
                        </Button>
                    </Box>

                    {/* Right summary & actions sidebar */}
                    <Box sx={{ width: { xs: '100%', md: 360 }, flexShrink: 0, mt: { xs: 2, md: 0 } }}>
                        <Box sx={{ position: { xs: 'static', md: 'sticky' }, top: { md: 16 } }}>
                            <Collapse in={showSummaryMobile} timeout={300}>
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.2,
                                        mb: 2,
                                        borderRadius: 2.5,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 10px rgba(17, 42, 86, 0.03)',
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Grid container spacing={1.2}>
                                        <InformationDocument order={order} color={badgeColor} />
                                    </Grid>
                                </Paper>
                            </Collapse>

                            {/* Action buttons / Status alert */}
                            {order?.canceled ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2.5,
                                        border: '1px solid #fecaca',
                                        bgcolor: '#fef2f2'
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#991b1b' }}>
                                        Orden Cancelada
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#b91c1c', display: 'block', mt: 0.5 }}>
                                        Esta orden fue marcada como cancelada y no requiere acciones de aprobación.
                                    </Typography>
                                </Paper>
                            ) : order?.saveOrderSap ? (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2.5,
                                        borderRadius: 2.5,
                                        border: '1px solid #bbf7d0',
                                        bgcolor: '#f0fdf4'
                                    }}
                                >
                                    <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#15803d' }}>
                                        Orden Procesada en SAP
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: '#166534', display: 'block', mt: 0.5 }}>
                                        Esta orden ya fue aprobada y sincronizada previamente con SAP.
                                    </Typography>
                                </Paper>
                            ) : (
                                <Paper
                                    elevation={0}
                                    sx={{
                                        p: 2,
                                        borderRadius: 2.5,
                                        border: '1px solid #e2e8f0',
                                        boxShadow: '0 2px 10px rgba(17, 42, 86, 0.03)',
                                        bgcolor: '#ffffff'
                                    }}
                                >
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.2 }}>
                                        <Button
                                            disabled={loading}
                                            variant="contained"
                                            startIcon={<CloseIcon />}
                                            onClick={() => { setConfirmAction('reject'); setOpenConfirm(true); }}
                                            sx={{
                                                width: '100%',
                                                height: 46,
                                                borderRadius: '50px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                bgcolor: '#ef4444',
                                                boxShadow: '0 4px 12px rgba(239, 68, 68, 0.25)',
                                                '&:hover': {
                                                    bgcolor: '#dc2626',
                                                    boxShadow: '0 6px 16px rgba(239, 68, 68, 0.35)'
                                                }
                                            }}
                                        >
                                            Rechazar
                                        </Button>
                                        <Button
                                            disabled={loading}
                                            variant="contained"
                                            startIcon={<CheckIcon />}
                                            onClick={() => { setConfirmAction('approve'); setOpenConfirm(true); }}
                                            sx={{
                                                width: '100%',
                                                height: 46,
                                                borderRadius: '50px',
                                                textTransform: 'none',
                                                fontWeight: 700,
                                                fontSize: '0.95rem',
                                                bgcolor: '#16a34a',
                                                boxShadow: '0 4px 12px rgba(22, 163, 74, 0.25)',
                                                '&:hover': {
                                                    bgcolor: '#15803d',
                                                    boxShadow: '0 6px 16px rgba(22, 163, 74, 0.35)'
                                                }
                                            }}
                                        >
                                            Aprobar
                                        </Button>
                                    </Box>
                                </Paper>
                            )}
                        </Box>
                    </Box>
                </Paper>
            </Container>

            <Dialog
                open={openConfirm}
                onClose={() => setOpenConfirm(false)}
                fullWidth
                maxWidth="sm"
                PaperProps={{ sx: { borderRadius: 3, p: 1 } }}
            >
                <DialogTitle sx={{ fontWeight: 800, color: '#112a56' }}>
                    {confirmAction === 'approve' ? 'Confirmar aprobación' : 'Confirmar rechazo'}
                </DialogTitle>
                <DialogContent dividers sx={{ borderColor: '#f1f5f9' }}>
                    <Typography sx={{ mb: 2, color: '#334155', fontSize: '1rem' }}>
                        {confirmAction === 'approve'
                            ? '¿Está seguro de que desea aprobar esta orden?'
                            : '¿Está seguro de que desea rechazar esta orden?'}
                    </Typography>

                    <Box sx={{ mt: 1 }}>
                        <Typography variant="caption" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, color: '#64748b' }}>
                            Notas registradas
                        </Typography>
                        <Paper
                            variant="outlined"
                            sx={{
                                p: 2,
                                mt: 0.5,
                                bgcolor: '#f8fafc',
                                borderRadius: 2,
                                borderColor: '#e2e8f0',
                                minHeight: 80,
                                whiteSpace: 'pre-wrap',
                                overflow: 'auto'
                            }}
                        >
                            <Typography variant="body2" color={text ? 'text.primary' : 'text.secondary'}>
                                {text && text.length > 0 ? text : 'Sin notas'}
                            </Typography>
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button
                        onClick={() => setOpenConfirm(false)}
                        disabled={loading}
                        sx={{ borderRadius: '50px', textTransform: 'none', px: 2.5, color: '#64748b' }}
                    >
                        Cancelar
                    </Button>
                    <Button
                        variant="contained"
                        disabled={loading}
                        onClick={() => {
                            if (confirmAction === 'approve') submitStatus(true);
                            else if (confirmAction === 'reject') submitStatus(false);
                        }}
                        sx={{
                            borderRadius: '50px',
                            textTransform: 'none',
                            px: 3,
                            fontWeight: 700,
                            bgcolor: confirmAction === 'approve' ? '#16a34a' : '#ef4444',
                            '&:hover': {
                                bgcolor: confirmAction === 'approve' ? '#15803d' : '#dc2626'
                            }
                        }}
                    >
                        {confirmAction === 'approve' ? 'Aprobar' : 'Rechazar'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default DetailDocument;